import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { currentOwnerId } from '../../auth.js';
import { energyOfDay } from '../../db/energy.js';

/**
 * Bilanz des Tages: Bedarf (mit Training, erledigt oder erwartet) gegen
 * Gegessenes. Eine Schätzung — sie steht deshalb klein unter den Balken, nicht
 * als Urteil darüber. Im Tag nur ohne Zielwerte (18.10.2026): Mit Ziel ist
 * das Ziel die eine kcal-Bezugsgröße, Bedarf und Bilanz daneben waren zwei
 * Zahlen zu viel.
 */
export function EnergyBalance({
  date,
  timeZone,
  quietSetup = false,
}: {
  date: string;
  timeZone: string;
  /** Fehlende Angaben nicht eigens melden — die Kachel „Zielwerte fehlen“ tut es schon. */
  quietSetup?: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const energy = useLiveQuery(
    () => energyOfDay(date, timeZone, db, new Date(), currentOwnerId()),
    [date, timeZone],
  );
  if (!energy) return null;
  if (!energy.ready) {
    if (quietSetup) return null;
    return <p className="kw-tile text-xs text-ink-muted">{t('energy.daySetup')}</p>;
  }
  const { balance } = energy;
  const sign = balance.balance > 0 ? '+' : balance.balance < 0 ? '−' : '±';
  return (
    <section aria-labelledby="energy-balance" className="kw-tile flex flex-col gap-1 text-sm">
      <h3 id="energy-balance" className="kw-kicker">
        {t('energy.dayHeading')}
      </h3>
      <p className="kw-figure text-2xl">
        {t('energy.dayBalance', { sign, kcal: Math.abs(balance.balance) })}
      </p>
      <p className="text-ink-muted">
        {t('energy.dayEaten', { kcal: balance.eaten })} ·{' '}
        {t('energy.dayNeed', { kcal: balance.need })}
      </p>
      {balance.doneKcal > 0 ? (
        <p className="text-xs text-ink-muted">{t('energy.dayDone', { kcal: balance.doneKcal })}</p>
      ) : null}
      {balance.plannedKcal > 0 ? (
        <p className="text-xs text-ink-muted">
          {t('energy.dayPlanned', { kcal: balance.plannedKcal })}
        </p>
      ) : null}
    </section>
  );
}
